// What a module shows: its routes, its tile in the rail, its trail rows,
// and the panels, tabs, settings and portal pieces the platform mounts
// for it. App.tsx provides the set; the chrome reads it here and never
// names a module screen by hand.

import { createContext, useContext, type ComponentType, type ReactElement } from "react";
import type { ModuleId } from "@duralex/shared";
import type { TrailStep } from "./trail";

export interface ModuleRoute {
  path: string;
  element: ReactElement;
  /** chrome = the staff shell behind RequireAuth, portal = behind
      RequireClientAuth, public = no wrapper at all. */
  wrapper: "chrome" | "portal" | "public";
}

/** A rail tile, which is also the palette's navigation action. */
export interface ModuleTile {
  id: string;
  icon: string;
  label: string;
  to: string;
  adminOnly?: true;
}

export interface ModuleTrailRow {
  path: string;
  steps: TrailStep[];
}

export interface MatterPanelProps {
  caseId?: string;
  clientEmail?: string;
}

export interface ClientTabProps {
  clientId: string;
  fixtures: boolean;
}

export interface ClientActionProps {
  /** The identifier the action may search by; none for a client known only by name. */
  lookupKey?: string;
  fixtures: boolean;
}

export interface ModuleScreens {
  id: ModuleId;
  routes: ModuleRoute[];
  tiles?: ModuleTile[];
  trails?: ModuleTrailRow[];
  matterPanels?: ComponentType<MatterPanelProps>[];
  clientTabs?: Array<{ key: string; label: string; panel: ComponentType<ClientTabProps> }>;
  clientActions?: ComponentType<ClientActionProps>[];
  /** Keyed by the integrations row whose gear opens them. */
  integrationSettings?: Record<string, () => ReactElement>;
  /** Each tab with its glyph: on a phone the strip is a bar of icons over words. */
  portalTabs?: Array<{ to: string; label: string; icon: ReactElement }>;
  portalCards?: ComponentType[];
}

export const ScreensContext = createContext<ModuleScreens[]>([]);

export function useScreens(): ModuleScreens[] {
  return useContext(ScreensContext);
}
